import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Check, MapPin, Phone, Mail, ExternalLink } from 'lucide-react';
import useSEO from '../../hooks/useSEO';
import './AboutUs.css';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const AboutUs = () => {
  useSEO({
    title: 'About Buy Sell Taxi – Commercial Vehicle Marketplace',
    description: "Learn about Buy Sell Taxi – India's trusted platform for buying and selling commercial vehicles. Explore our BST Yards across India, our team & our commitment to transparent vehicle transactions.",
    keywords: 'about buy sell taxi, BST yards India, commercial vehicle dealer India, trusted vehicle marketplace, buy taxi company India',
    url: 'https://www.buyselltaxi.com/about',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      'name': 'About Buy Sell Taxi',
      'description': "India's trusted marketplace for buying and selling commercial vehicles",
      'url': 'https://www.buyselltaxi.com/about',
      'publisher': {
        '@type': 'Organization',
        'name': 'Buy Sell Taxi',
        'url': 'https://www.buyselltaxi.com'
      }
    }
  });
  const [content, setContent] = useState(null);
  const [yards, setYards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/buyer/about`)
      .then(res => {
        setContent(res.data.content);
        // `|| []` is not belt-and-braces: any 200 whose body is not the JSON
        // this expects (an SPA-fallback HTML page, a gateway error page) makes
        // this undefined, and `yards.length` below then throws during render.
        // React unmounts the whole tree on that, so the prerenderer captured an
        // empty <div id="root"> and shipped a blank /about to crawlers.
        setYards(res.data.yards || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading">Loading...</div>;

  let whyPoints = [];
  try { whyPoints = content?.why_choose_bst ? JSON.parse(content.why_choose_bst) : []; } catch { whyPoints = []; }

  return (
    <div className="about-page" data-testid="about-page">
      {/* Hero */}
      <section className="about-hero">
        <h1>About Buy Sell Taxi</h1>
        <p>India's trusted commercial vehicle marketplace</p>
      </section>

      {/* Who We Are */}
      {content?.who_we_are && (
        <section className="about-section" data-testid="who-we-are">
          <h2>Who We Are</h2>
          <p>{content.who_we_are}</p>
        </section>
      )}

      {/* Our Mission */}
      {content?.our_mission && (
        <section className="about-section" data-testid="our-mission">
          <h2>Our Mission</h2>
          <p>{content.our_mission}</p>
        </section>
      )}

      {/* Why Choose BST */}
      {whyPoints.length > 0 && (
        <section className="about-section why-section" data-testid="why-choose-bst">
          <h2>Why Choose BST</h2>
          <div className="why-grid">
            {whyPoints.map((point, i) => (
              <div key={i} className="why-card">
                <Check size={20} className="why-check" />
                <span>{point}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Founders */}
      {content?.founder_name && (
        <section className="about-section founders" data-testid="founders-section">
          <h2>Meet Our Founder</h2>
          <div className="founder-card">
            {content.founder_photo && (
              <img src={content.founder_photo} alt={content.founder_name} className="founder-photo" loading="lazy" decoding="async" />
            )}
            <div className="founder-info">
              <h3>{content.founder_name}</h3>
              <span className="founder-title">{content.founder_title}</span>
              {content.founder_bio && <p>{content.founder_bio}</p>}
            </div>
          </div>
        </section>
      )}

      {/* BST Yards */}
      {yards.length > 0 && (
        <section className="about-section" data-testid="bst-yards-section">
          <h2>Our BST Yards</h2>
          <div className="yards-grid">
            {yards.map((y, i) => (
              <div key={i} className="yard-card" data-testid={`yard-card-${i}`}>
                <h3><MapPin size={16} /> {y.name}</h3>
                <p>{y.address_line}, {y.city}, {y.state} {y.pincode}</p>
                <div className="yard-actions">
                  {y.contact_phone && (
                    <a href={`tel:${y.contact_phone}`} className="yard-link">
                      <Phone size={14} /> {y.contact_phone}
                    </a>
                  )}
                  {y.google_maps_link && (
                    <a href={y.google_maps_link} target="_blank" rel="noopener noreferrer" className="yard-link">
                      <ExternalLink size={14} /> View on Map
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Contact */}
      <section className="about-section contact-section" data-testid="contact-section">
        <h2>Contact Us</h2>
        <div className="contact-cards">
          {content?.platform_phone && (
            <a href={`tel:${content.platform_phone}`} className="contact-card">
              <Phone size={24} />
              <span>{content.platform_phone}</span>
            </a>
          )}
          {content?.platform_email && (
            <a href={`mailto:${content.platform_email}`} className="contact-card">
              <Mail size={24} />
              <span>{content.platform_email}</span>
            </a>
          )}
          <a href="https://wa.me/919560759834" target="_blank" rel="noopener noreferrer" className="contact-card whatsapp-card">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="#25D366">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
            <span>Chat on WhatsApp</span>
          </a>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
